import React, { PropsWithChildren } from "react";

import { css, cx } from "@emotion/css";
import { Controller, FieldPath, FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import { FormInputError } from "@/uiKit/atoms/forms/FormInputError.tsx";
import { SelectInput, type SelectInputProps } from "@/uiKit/atoms/inputs/SelectInput.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { retrieveFormError } from "@/utils/formUtils.ts";

export type BaseFormSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = PropsWithChildren<{
  name: TName;
  label?: string;
  selectClassName?: string;
  selectClasses?: SelectInputProps["classes"];
  fullWidth?: boolean;
  reserveErrorSpace?: boolean;
  disabled?: boolean;

  onClick?: React.MouseEventHandler<HTMLDivElement>;
}>;

export type FormSelectProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = AtomProps<
  BaseFormSelectProps<TFieldValues, TName>,
  StyleClasses<"root" | "content" | "select" | "error" | "label">
>;

export function FormSelectInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  name,
  label,
  className,
  classes,
  selectClassName,
  selectClasses,
  reserveErrorSpace = false,
  ...props
}: FormSelectProps<TFieldValues, TName>) {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  return (
    <Controller<TFieldValues, TName, TTransformedValues>
      render={({ field: { onChange, value, onBlur, disabled }, fieldState }) => {
        const formError = retrieveFormError(fieldState.error);
        return (
          <div className={cx(styles.container, className)}>
            <div className={cx(styles.content, classes?.content)}>
              {!!label && (
                <Typography
                  variant="body"
                  className={cx({ [styles.label]: true, [styles.labelError]: formError.hasError }, classes?.label)}
                >
                  {label}
                </Typography>
              )}
              <SelectInput
                value={value}
                className={cx(styles.select, selectClassName)}
                onBlur={() => onBlur()}
                disabled={disabled}
                error={formError.hasError}
                onChange={(_, event) => onChange(event)}
                classes={{
                  ...selectClasses,
                  root: cx(classes?.select, selectClasses?.root),
                }}
                {...props}
              />
            </div>
            <FormInputError
              reserveSpace={reserveErrorSpace}
              errorMessage={formError.errorMessage ? t(formError.errorMessage) : undefined}
              className={cx(styles.error, classes?.error)}
            />
          </div>
        );
      }}
      name={name}
    />
  );
}

const makeStyles = (theme: Theme) => ({
  container: css({
    minWidth: 0,
  }),
  content: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: theme.spacing(6),
    overflow: "clip",
  }),
  select: css({
    minWidth: 0,
  }),
  error: css({}),
  label: css({
    textWrap: "nowrap",
    overflow: "clip",
    textOverflow: "ellipsis",
  }),
  labelError: css({
    color: theme.palette.error.main,
  }),
});
